/**
 * validator.js
 */

import { FeatureFlags } from '../featureFlags';
import widgets from '../legacy/widgets';
import { reservedUuids, ValidatorErrorTypes } from "./constants";
import { ApplicationTypes } from './constants/ApplicationTypes';
import { validateOneFsmLimit, validateFsm, validateSequences } from './validators';
import { action, computed, makeObservable, observable } from "mobx";

/**
 * The validator error object
 * @typedef {Object} ErrorObject
 * @property {string} header - the header of the error
 * @property {Object} onError - the error message and options
 * @property {string} onError.message - the error message
 * @property {Array} onError.options - the options to fix the error
 * @property {string} onError.options.message - the message of the option
 * @property {string} onError.options.target - the target of the option
 * @property {Object} onSuccess - the success message
 * @property {string} onSuccess.message - the success message
 * @property {string} [instructionSubResource] - the sub resource of the instruction. Useful for instructions with multiple errors like fsm or if else
 */

const DCT_FLOW_FEATURE_ENABLED = FeatureFlags.DIGITAL_CALIBRATION_FLOW;

export function Validator(pLibrary) {
  this.mLibrary = pLibrary;
  this.mCache = {};
  this.mNotFlattenCache = {};
  this.mHasRun = false;
  this.mValidateAppCallbacks = [];

  makeObservable(this, {
    mLibrary: observable,
    mCache: observable,
    mNotFlattenCache: observable,
    mHasRun: observable,
    cache: computed,
    validate: action,
    validateApp: action,
    runValidation: action,
  });
}

/**
 * Returns the list of validateable applications
 * @param {import('../machinelogic/Library').Library} library
 * @returns {import('../machinelogic/ApplicationBase').ApplicationBase[]}
 */
function getValidateableApplications(library) {
  return library.getAllApplications().filter(application => application.isValidateable);
}


// Get a flat list of all instructions for an app (will flatten out the sequences)
Validator.prototype.getInstructionsByAppName = function (pApplicationName = null) {
  let lApps = getValidateableApplications(this.mLibrary);
  if (pApplicationName) {
    lApps = [lApps.find(function (pEl) { return pEl.mName === pApplicationName; })];
  }

  return this._getInstructionsWithAppList(lApps);
};

Validator.prototype._getInstructionsWithAppList = function (pApps = null) {
  const lInstructions = [];
  if (!pApps) pApps = getValidateableApplications(this.mLibrary);

  pApps.forEach(function (pApp) {
    pApp.mSequences.forEach(function (pSeq) {
      pSeq.mInstructions.forEach(function (pInst, pIndex) {
        lInstructions.push({ instruction: pInst, index: pIndex, seq: pSeq.mName, app: pApp.mName });
      });
    });
  });

  return lInstructions;
};

Validator.prototype.instructionsUsingController = function (pController) {
  const lThis = this;
  const lInvalidInstructions = [];
  pController.getConfig().listAxes().forEach(function (pAxis) { lInvalidInstructions.push(...lThis.instructionsUsingActuators(pAxis)); });
  pController.getConfig().listInputs().forEach(function (pInput) { lInvalidInstructions.push(...lThis.instructionsUsingInputsOutputs('Input', pInput)); });
  pController.getConfig().listOutputs().forEach(function (pOutput) { lInvalidInstructions.push(...lThis.instructionsUsingInputsOutputs('Output', pOutput)); });
  return lInvalidInstructions;
};


Validator.prototype.instructionsUsingSequence = function (pSequenceName, pApp = null) {
  // get all instruction referencing the sequence name
  const lInstructions = this._getInstructionsWithAppList(pApp ? [pApp] : null);

  const lInvalids = lInstructions.filter(function (pInst) {
    if ('if' === pInst.instruction.mName) {
      return pInst.instruction.getParameter('conditions').some(function (pCondition) {
        return pCondition.sequenceName === pSequenceName;
      });
    }
    if('fsm' === pInst.instruction.mName) {
      const lStates = pInst.instruction.getParameter('states');
      return lStates.find(pState => pState.actionSequenceName === pSequenceName);
    }
    // everything else that is not if-conditions
    const lName = pInst.instruction.getParameter('sequenceName');
    if (!lName) { return false; }
    return lName === pSequenceName;
  });

  return lInvalids;
};


Validator.prototype.instructionsUsingActuators = function (pAxis, pApplicationName = null) {
  // get all instruction referencing the actuator name
  const lAxisUUID = pAxis.getUUID();
  const lInstructions = this.getInstructionsByAppName(pApplicationName);
  const lInvalids = lInstructions.filter(function (pInst) {
    if ("setVariable" === pInst.instruction.mCategory) {
      const lAxis = pInst.instruction.getParameter('inputUuid');
      // using includes because "_push" and "_pull" get appended to uuid
      if (lAxis) { return lAxis.includes(lAxisUUID); }
    }
    if ('motor' == pInst.instruction.mCategory || ['fromActuatorPosition', 'motionCompletion'].includes(pInst.instruction.mName)) {
      const lAxis = pInst.instruction.getParameter('axis');
      if (lAxis) { return lAxis === lAxisUUID; }

      const lAxes = pInst.instruction.getParameter('axes');
      if (!lAxes) { return false; }

      for (let i = 0; i < lAxes.length; i++) {
        if (pInst.instruction.getParameter(`axes.${i}.axis`) === lAxisUUID) { return true; }
      }
    }
    return false;
  });
  return lInvalids;
};


Validator.prototype.instructionsDependingOnActuatorType = function (pActuator, pType, pApplicationName = null) {
  // get all instruction referencing the actuator name
  const lAxisUUID = pActuator.getUUID();
  const lInstructions = this.getInstructionsByAppName(pApplicationName);
  const lInvalids = lInstructions.filter(function (pInst) {
    let lAllowedActuatorsTypes = [];

    // handle fromDevice instructions that depend on actuators
    if (pInst.instruction.getName() === "fromDevice") {
      // using includes because "_push" and "_pull" get appended to uuid
      const lAxis = pInst.instruction.getParameter("inputUuid");
      if (!lAxis.includes(lAxisUUID)) { return false; }
      return !pType.includes('pneumatic');
    }

    if (['setSpeed', 'setIndexerAngle', 'setAcceleration'].includes(pInst.instruction.mName)) {
      console.warn('This Instruction does not have [allowedAxisType] data. Assuming it is good.', pActuator, pType, pInst.instruction.mName, pInst);
      return false;
    }

    if (['stopAllMotion'].includes(pInst.instruction.mName)) {
      // always pass, so we will pretend that no one is using it
      return false;
    }

    const lAxis = pInst.instruction.getParameter('axis');
    if (lAxis) {
      if (lAxis !== lAxisUUID) { return false; }
      lAllowedActuatorsTypes = pInst.instruction._getInstructionModel().parameters.find(item => item.name === "axis").allowedAxisType;
      return !lAllowedActuatorsTypes.includes(pType);
    }

    const lAxes = pInst.instruction.getParameter('axes');
    if (!lAxes) { return false; }

    for (let i = 0; i < lAxes.length; i++) {
      const lActuator = lAxes[i].axis;
      if (lActuator === lAxisUUID) {
        if (pInst.instruction.mName.match(/moveRelative/)) {
          lAllowedActuatorsTypes = pInst.instruction.mInstructionSet.motor.moveRelative.parameters[0].allowedAxisType;
        }
        else if (pInst.instruction.mName.match(/moveAbsolute/)) {
          lAllowedActuatorsTypes = pInst.instruction.mInstructionSet.motor.moveAbsolute.parameters[0].allowedAxisType;
        }
        return !lAllowedActuatorsTypes.includes(pType);
      }
    }
    return false;

  });
  return lInvalids;
};


Validator.prototype.instructionsUsingInputsOutputs = function (pType, pIO, pApplicationName = null) {
  const lLibrary = this.mLibrary;
  const lUUID = pIO.getUUID();

  const lInstructions = this.getInstructionsByAppName(pApplicationName);
  // get all instruction referencing the inputs/outputs
  const lInvalids = lInstructions.filter(function (pEl) {
    if (pType == 'Output') {
      if (pEl.instruction.mCategory == 'output') {
        if (pEl.instruction.getParameter('outputUuid') === lUUID) { return true; }
      }
    }
    else if (pType == 'Input') {
      const lInstructionCat = pEl.instruction.mCategory;
      const lInstructionName = pEl.instruction.mName;

      // if input is linked to the instruction mark as invalid
      if (lInstructionCat == 'waitFor' && ['digitalInput', 'digitalInputEdge'].includes(lInstructionName)) {
        if(pEl.instruction.getParameter('inputUuid') === lUUID) { return true; }
      }
      else if(lInstructionCat === 'fsm') {
        const lSates = pEl.instruction.getParameter('states');
        const lInstructionUsingInputUuid = lSates.find(pState => {
          return pState.targetStates.find(pTargetState => pTargetState.eventType === 'digital_input' && pTargetState.eventDefinition.includes(lUUID)); // eventDefinition looks like "inputUuid|value" i.e. // "65834b14-4a7a-45fa-b332-f0cf89e40ec9|0"
        });
        if(lInstructionUsingInputUuid) { return true; }
      }
      else if (lInstructionCat == 'setVariable' && lInstructionName == 'fromDevice') {
        if(pEl.instruction.getParameter('inputUuid') === lUUID) { return true; }
      }
      else if (lInstructionCat == 'condition' ||
                (lInstructionCat == 'setVariable' && lInstructionName == 'fromExpression') ||
                (lInstructionCat == 'log' && lInstructionName == 'console')
      ) {
        // scan the expression if they contain a variable that is linked to the selected input
        let lIsVariableUsed = false;
        getValidateableApplications(lLibrary).forEach((pApp) => {
          // FIXME: because we dont leave the forEach loop earlier, we will do unnecessary processing, this could be improved
          if (pEl.app == pApp.getName()) {
            const lLinkedVariable = Object.values(pApp.mParameters.mParameters).find((pVar) => {
              if (pVar.type == 'input' && pVar.inputUuid == lUUID) { return true; }
            });
            if (lLinkedVariable != undefined) {
              if (lInstructionCat == 'setVariable') {
                if (pEl.instruction.getParameter('expression').includes(lLinkedVariable.name)) { lIsVariableUsed = true; }
              }
              else if(lInstructionCat == 'log') {
                // we will get all the possible variable names that are using this input
                // and then check if any of them are in log.message
                const lParams = pEl.instruction.mApp.mParameters.mParameters;
                const lLinkedVariableNames = Object.values(lParams).filter(function (pVariable) {
                  if (!pVariable.hasOwnProperty('inputUuid')) { return false; }
                  return pVariable.inputUuid == lUUID;
                })
                  .map(function(pVariable) { return pVariable.name; });

                const lInstructionMessage = pEl.instruction.getParameter('message');
                lLinkedVariableNames.forEach(function (pName) {
                  // variables in the log message are in this form ` Some quantity: ${VARIABLE_NAME} !`
                  if (lInstructionMessage.match(new RegExp('\\${' + pName + '}'))) { lIsVariableUsed = true; }
                });
              }
              else {
                pEl.instruction.getParameter('conditions').forEach((pCond) => {
                  if (pCond.conditionExpression.includes(lLinkedVariable.name)) { lIsVariableUsed = true; }
                });
              }
            }
          }
        });
        return lIsVariableUsed;
      }
    }
    return false;
  });
  return lInvalids;
};


Validator.prototype._flattenWarnings = function (pWarnings) {
  // this will return an array of errors for the App
  let lResult = [];
  for (const lSeqName in pWarnings) {
    pWarnings[lSeqName].forEach(function (pEl) { lResult.push(pEl); });
  }
  lResult = lResult.filter(function (pEl) { return !!pEl; });
  return lResult;
};


Validator.prototype._processErrorDiffs = function (pApp, pCachedErrors, pNewErrors) {

  function findErrorWithSameSignatureFromList(pErrorSignature, pErrorsList) {
    return pErrorsList.find(function (pEl) {
      if (pEl.type === ValidatorErrorTypes.ApplicationError) {
        return pEl.key == pErrorSignature.key;
      }
      else if (pEl.type === ValidatorErrorTypes.InstructionError) {
        return (pEl.instruction == pErrorSignature.instruction) && (pEl.instructionSubResource == pErrorSignature.instructionSubResource);
      }
      else {
        console.error(`validator error type ${pEl.type} not supported`);
      }
    });
  }
  function isNew(pError, pCachedErrors) {
    const lError = findErrorWithSameSignatureFromList(pError, pCachedErrors);
    return !lError;
  }

  function isChanged(pError, pNewErrors) {
    const lError = findErrorWithSameSignatureFromList(pError, pNewErrors);

    if (!lError) { return false; }
    if (lError.app !== pError.app) { return true; }
    if (lError.seq !== pError.seq) { return true; }
    if (lError.index !== pError.index) { return true; }
    if (lError.header !== pError.header) { return true; }
    if (JSON.stringify(lError.onError) !== JSON.stringify(pError.onError)) { return true; }
    if (JSON.stringify(lError.onSuccess) !== JSON.stringify(pError.onSuccess)) { return true; }
    return false;
  }

  // FIXME: NOTE: if we delete an instruction, it will be seen as resolved,
  // will need to test for actual deletion
  function isResolved(pError, pNewErrors) {
    const lError = findErrorWithSameSignatureFromList(pError, pNewErrors);
    return !lError;
  }

  pCachedErrors.forEach(function (pCachedError) {
    // process resolved
    if (isResolved(pCachedError, pNewErrors)) {
      pApp.notify('onErrorResolved', pCachedError, pNewErrors);
      if (pCachedError.type === ValidatorErrorTypes.InstructionError) {
        const lInstructionContainsOtherErrors = pNewErrors.some(function (pNewErrors) { return pNewErrors.instruction == pCachedError.instruction; });
        pCachedError.instruction.notify('onInstructionErrorStatusUpdate', lInstructionContainsOtherErrors);
      }
      return;
    }
    // process changes
    const lUpdated = isChanged(pCachedError, pNewErrors);
    if (lUpdated) {
      const lError = findErrorWithSameSignatureFromList(pCachedError, pNewErrors);
      pApp.notify('onErrorUpdated', pCachedError, lError);
    }
  });

  // process new
  pNewErrors.forEach(function (pNewError) {
    if (isNew(pNewError, pCachedErrors)) {
      pApp.notify('onErrorCreated', pNewError, pNewErrors);
      if (pNewError.type === ValidatorErrorTypes.InstructionError) {
        pNewError.instruction.notify('onInstructionErrorStatusUpdate', true);
      }
    }
  });
};


Validator.prototype.clearCacheForApp = function (pAppName) {
  if (this.mCache[pAppName] != undefined) {
    delete this.mCache[pAppName];
    delete this.mNotFlattenCache[pAppName];
    this.mLibrary.notify('onErrorCountChanged', this.getTotalErrors());
  }
};

Validator.prototype.getAllWarningsForApp = function (pAppName) {
  const result = this.validateApp(pAppName);
  return this._flattenWarnings(result);
};

Validator.prototype.getAllWarningsForAppFromCache = function (pAppName) {
  return this.mCache[pAppName];
};

// NOTE: this is a naive implementation
// we only check that each used actuator has a set speed/acceleration
Validator.prototype.validateSpeedAccelerationPresence = function (pApp) {
  const uuidMap = {
    // hold actuator that have speed./acceleration configured
    actuatorSetSpeed: new Set(),
    actuatorSetAcceleration: new Set(),
    // hold system wide speed/acceleration configuration
    systemSetSpeed: false,
    systemSetAcceleration: false,
    // hold actuators that need to be configured, for simplicity we treat them as actuator-speed and actuator-acceleration pairs
    actuatorUsingSpeed: new Set(),
    actuatorUsingAcceleration: new Set(),
  };

  pApp.mSequences.forEach(function (pSeq) {
    pSeq.mInstructions.forEach(function (pInst, pIndex) {
      const lName = pInst.mName;
      const lParams = pInst.mParameters;

      if (['moveAbsolute', 'moveRelative', 'moveToClosestAngle'].includes(lName)) {
        uuidMap.actuatorUsingSpeed.add(lParams.axis, lName);
        uuidMap.actuatorUsingAcceleration.add(lParams.axis, lName);
      }
      else if (['moveAbsoluteCombined', 'moveRelativeCombined'].includes(lName)) {
        lParams.axes.forEach((axis) => {
          if (!axis.axis) { return; }
          uuidMap.actuatorUsingSpeed.add(axis.axis, lName);
          uuidMap.actuatorUsingAcceleration.add(axis.axis, lName);
        });
      }
      else if (['setActuatorSpeed', 'setActuatorAcceleration'].includes(lName)) {
        switch (lName) {
          case 'setActuatorSpeed': uuidMap.actuatorSetSpeed.add(lParams.axis, lName); break;
          case 'setActuatorAcceleration': uuidMap.actuatorSetAcceleration.add(lParams.axis, lName); break;
          default: break;
        }
      }
      else if (['setSpeed', 'setAcceleration'].includes(lName)) {
        switch (lName) {
          case 'setSpeed': uuidMap.systemSetSpeed = true; break;
          case 'setAcceleration': uuidMap.systemSetAcceleration = true; break;
          default: break;
        }
      }
    });
  });

  // clear the speeds
  if (uuidMap.systemSetSpeed) {
    uuidMap.actuatorSetSpeed = new Set();
    uuidMap.actuatorUsingSpeed = new Set();
  }
  // clear the acceleration
  if (uuidMap.systemSetAcceleration) {
    uuidMap.actuatorSetAcceleration = new Set();
    uuidMap.actuatorUsingAcceleration = new Set();
  }

  // keep only the actuators that dont have speed/acceleration set
  uuidMap.actuatorSetSpeed.forEach((key) => { uuidMap.actuatorUsingSpeed.delete(key); });
  uuidMap.actuatorSetAcceleration.forEach((key) => { uuidMap.actuatorUsingAcceleration.delete(key); });

  // generate the errors messages
  const result = [];
  uuidMap.actuatorUsingSpeed.forEach((key) => {
    const axis = this.mLibrary.getConfigManager().getAxisByUuid(key);
    if (!axis) { return; }
    result.push({
      header: `Speed is not set for actuator: ${axis.getName()}`,
      onError: {
        message: "It is recommended to set the speed of an actuator before moving it.",
        options: [{ message: 'Add a `Set Speed` command for this actuator.', target: 'none' }]
      },
      onSuccess: {
        message: "Speed successfully set."
      },
      type: ValidatorErrorTypes.ApplicationError,
      key: key + '/speed',
      app: pApp.mName,
      seq: pApp.mInitialSequence.mName,
      id: pApp.mId
    });
  });

  uuidMap.actuatorUsingAcceleration.forEach((key) => {
    const axis = this.mLibrary.getConfigManager().getAxisByUuid(key);
    if (!axis) { return; }
    result.push({
      header: `Acceleration is not set for actuator: ${axis.getName()}`,
      onError: {
        message: "It is recommended to set the acceleration of an actuator before moving it.",
        options: [{ message: 'Add a `Set Acceleration` command for this actuator.', target: 'none' }]
      },
      onSuccess: {
        message: "Acceleration successfully set."
      },
      type: ValidatorErrorTypes.ApplicationError,
      key: key + '/acceleration',
      app: pApp.mName,
      seq: pApp.mInitialSequence.mName,
      id: pApp.mId
    });
  });

  return result;
};


Validator.prototype._updateAppNameInCache = function (pApp) {
  if (!this.mCache[pApp.getName()]) {
    const originalAppName = Object.keys(this.mCache).find((appName) => {
      if (!this.mCache[appName].length) { return false; }
      return this.mCache[appName][0].id == pApp.mId;
    });
    if (originalAppName && originalAppName !== pApp.mName) {
      this.mCache[pApp.mName] = this.mCache[originalAppName];
      delete this.mCache[originalAppName];
    }
  }
};


Validator.prototype.validateApp = function (pApp) {
  // this will return all errors for each app and sequence in the form of: app.seq[instructions]
  const result = {};
  const lThis = this;

  if (!pApp) {
    console.error(`Failed to validate application: ${pApp}`);
    return;
  }

  // PythonApplications are not validated for the time being.
  if (pApp.getType() === ApplicationTypes.Python) {
    return;
  }

  // make sure that each actuator has a corresponding speed/acceleration set instruction
  // those errors are treated differently as they dont correspond to any particular instruction
  result.__speedAccelerationCheck = this.validateSpeedAccelerationPresence(pApp);
  result.__oneFsmLimitCheck = this.validateOneFsmLimit(pApp);

  if ( DCT_FLOW_FEATURE_ENABLED ) {
    for ( const callbackName in this.mValidateAppCallbacks) {
      result["__" + callbackName] = this.mValidateAppCallbacks[callbackName](pApp, ValidatorErrorTypes);
    }
  }

  const lAppName = pApp.getName();
  pApp.mSequences.forEach(function (pSeq) {
    const lInstructions = [];
    pSeq.mInstructions.forEach(function (pInst, pIndex) {
      // NOTE: an instruction can have multiple errors
      let lValidation;
      try {
        lValidation = lThis.validate(pInst, pApp.mName, pSeq.mName, pIndex);
      }
      catch (error) {
        // let's not crash the app if a validation fails.
        console.warn('Error while validating, drop the validation for this instruction.', pInst, pApp.mName, pSeq.mName, pIndex);
        console.error(error);
        return;
      }
      if (!lValidation) { return; }
      if (!Array.isArray(lValidation)) { lValidation = [lValidation]; } // for simplicity, we will process everything as an array
      lValidation.forEach(function (pValidation) {
        pValidation.instruction = pInst;
        pValidation.app = pApp.mName;
        pValidation.seq = pSeq.mName;
        pValidation.index = pIndex;
        pValidation.id = pApp.mId;
        pValidation.type = ValidatorErrorTypes.InstructionError;

        lInstructions.push(pValidation);
      });
    });
    result[pSeq.mName] = lInstructions;
  });

  // we might renaming the application, find the old one, and update the cache name
  this._updateAppNameInCache(pApp);

  // Process state change in errors and launch notifications (error widget card listens to this notification)
  const lNewErrors = this._flattenWarnings(result);
  const lCachedErrors = Object.assign({}, this.mCache);
  this._processErrorDiffs(pApp, lCachedErrors[lAppName] != undefined ? lCachedErrors[lAppName] : [], lNewErrors);

  this.mCache[lAppName] = lNewErrors;
  this.mNotFlattenCache[lAppName] = result; // we need this to notify via 'onValidatorResult' later, when documents are rebuilt

  // TreeView listens to these notifications
  pApp.notify('onValidatorResult', result);

  // NOTE: we could "lFlattenResult.forEach(function(pEl) { pEl.instruction.notify('onInstructionErrorStatusUpdate', true); });" here
  // Currently we are doing this in the _processErrorDiffs as error are created. For this to work we have to manually send those notifications when opening the document (CAD )
  // otherwise the notifications will be sent while UI is not built. If you for some reason it is not sync, start by notify here, and see.

  // Left pane error count listens to this notification;
  this.mLibrary.notify('onErrorCountChanged', this.getTotalErrors());

  // this is useful on the configuration page, got floating card
  this.mLibrary.getConfigManager().notify('onValidatorResult', lNewErrors);
};


Validator.prototype.runValidation = function () {
  // NOTE: we currently capture the previous state here,
  // NOTE: that means that if someone changed the cache we might miss some events
  // FIXME: maybe capture at the end
  const lThis = this;
  getValidateableApplications(this.mLibrary).forEach(function (pApp) {
    lThis.validateApp(pApp);
  });

  this.mHasRun = true;
  this.mLibrary.notify('onLibraryValidated');
};

Validator.prototype.invalidate = function () {
  this.mCache = {};
  this.mHasRun = false;
};

Validator.prototype.getTotalErrors = function () {
  let total = 0;
  for (const appName in this.mCache) {
    total += this.mCache[appName].length;
  }

  return total;
};

Validator.prototype._validateCombinedMoves = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {

  // NOTE: the combination of an unsupported type and missed axis will only show one error at a time
  const lConfigManager = this.mLibrary.getConfigManager();

  // Incompatible motion type
  const lCheckIfValidType = (pElement) => {
    const lAxis = lConfigManager.getAxisByUuid(pElement.axis);
    if (!lAxis) { return; }
    const lAllowedAxis = pInstruction._getInstructionModel().parameters[0].unit[0].allowedAxisType;
    if (!lAllowedAxis.includes(lAxis.getType())) {
      return {
        header: "Incompatible Motion type.",
        onError: {
          message: `"${pSequenceName}" → command #${pInstructionIndex}`,
          options: [
            { message: 'Select available Motion from the drop down menu.', target: 'motion' },
            { message: 'Select an Actuator from the drop down menu.', target: `${pElement.position}.actuator` }
          ]
        },
        onSuccess: {
          message: 'The Motion type is now compatible with the Actuator.'
        },
        instructionSubResource: pElement.position
      };
    }
  };

  // missing axis
  const lCheckIfActuatorUnknown = (pElement) => {
    if (pElement.axis === reservedUuids.UNKNOWN || !lConfigManager.getAxisByUuid(pElement.axis)) {
      return {
        header: `Missing associated Actuator`,
        onError: {
          message: `"${pSequenceName}" → command #${pInstructionIndex}`,
          options: [
            { message: 'Select an Actuator from the drop down menu.', target: `${pElement.position}.actuator` },
            { message: `Add an Actuator in the Configuration tab.`, target: 'redirect-config' }
          ]
        },
        onSuccess: {
          message: 'The Actuator is not missing anymore.'
        },
        instructionSubResource: pElement.position
      };
    }
  };

  const lAxes = pInstruction.mParameters.axes.reduce(function (pAccumulator, pCurrent, pIndex) {
    if (pCurrent.axis) { pAccumulator.push({ axis: pCurrent.axis, position: pIndex }); }
    return pAccumulator;
  }, []);

  const lErrors = lAxes.reduce(function (pAccumulator, pElement) {
    const lUnknownError = lCheckIfActuatorUnknown(pElement);
    const lTypeError = lCheckIfValidType(pElement);

    if (lUnknownError) { pAccumulator.push(lUnknownError); }
    if (lTypeError) { pAccumulator.push(lTypeError); }

    return pAccumulator;
  }, []);

  if (lErrors.length) { return lErrors; }

  return null;
};


Validator.prototype._validateSingleMove = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  const lConfigManager = this.mLibrary.getConfigManager();
  const lAxisUUID = pInstruction.mParameters.axis;

  // Missing associated axis UUID
  if (reservedUuids.NONE == lAxisUUID || reservedUuids.UNKNOWN == lAxisUUID || !lConfigManager.getAxisByUuid(lAxisUUID)) {
    return {
      header: 'Missing associated Actuator.',
      onError: {
        message: `"${pSequenceName}" → command #${pInstructionIndex}`,
        options: [
          { message: 'Select an Actuator from the drop down menu.', target: 'actuator' },
          { message: `Add an Actuator in the Configuration tab.`, target: 'redirect-config' }
        ]
      },
      onSuccess: {
        message: 'You do not have any missing Actuator.'
      }
    };
  }

  // Incompatible motion type
  const lAxis = lConfigManager.getAxisByUuid(lAxisUUID);
  const lAllowAxisType = pInstruction._getInstructionModel().parameters.find(item => item.name === "axis").allowedAxisType;
  const lTestValidActuatorType = lAllowAxisType.includes(lAxis.getType());
  if (!lTestValidActuatorType) {
    const lOptions = [{ message: 'Select available Motion from the drop down menu.', target: 'motion' }];
    if (lConfigManager.numberOfRealAxes() > 1) { lOptions.push({ message: 'Select another Actuator from the drop down menu.', target: 'actuator' }); }
    return {
      header: 'Incompatible Motion type.',
      onError: {
        message: `"${pSequenceName}" → command #${pInstructionIndex}`,
        options: lOptions
      },
      onSuccess: {
        message: 'The Motion type is now compatible with the Actuator.'
      }
    };
  }
  return null;
};

Validator.prototype._validateNonMotorAxisInstruction = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  const lConfigManager = this.mLibrary.getConfigManager();
  let lAxisUUID = pInstruction.mParameters.axis;

  if (!lAxisUUID && pInstruction.mName === "motionCompletion") {
    lAxisUUID = reservedUuids.ALL;
  }

  // Individual axis validation is not necessary for instructions performed on all
  if (lAxisUUID === reservedUuids.ALL) {
    return null;
  }

  // Missing associated axis UUID
  const lIsMissingAssociatedAxisUUID = reservedUuids.NONE == lAxisUUID || reservedUuids.UNKNOWN == lAxisUUID || !lConfigManager.getAxisByUuid(lAxisUUID);

  // Incompatible actuator type
  const lAxis = lConfigManager.getAxisByUuid(lAxisUUID);
  const lAxisParameters = pInstruction._getInstructionModel().parameters.find(item => item.name === "axis");
  const lCompatibleAxisList = pInstruction._listCompatibleAxisUuids(lAxisParameters);
  const lIsMotionCompatible = lAxis != undefined ? lAxisParameters.allowedAxisType.includes(lAxis.getType()) : undefined;

  if (lIsMissingAssociatedAxisUUID || !lIsMotionCompatible) {
    let lHeader; let lSuccess;
    if (lIsMissingAssociatedAxisUUID) {
      lHeader = 'Missing associated Actuator.';
      lSuccess = 'You do not have any missing Actuator.';
    }
    else { // Motion incompatible
      lHeader = 'Incompatible Actuator type.';
      lSuccess = 'The Actuator type is now compatible with this instruction.';
    }
    // For use in this function only
    const categoryToDropDownName = {
      "waitFor": "Wait For",
      "setVariable": "Set Variable"
    };
    const categoryDropdownName = categoryToDropDownName[pInstruction.mCategory];
    const dropDownName = categoryDropdownName || widgets.camelCaseToText(pInstruction.mCategory);
    const lOptions = [
      { message: `Select a different command from the ${dropDownName} drop down.`, target: pInstruction.mCategory },
      { message: `Add an Actuator in the Configuration tab.`, target: 'redirect-config' },
    ];

    if (lCompatibleAxisList.length >= 1) { lOptions.push({ message: 'Select another Actuator from the drop down menu.', target: 'actuator' }); }

    return {
      header: lHeader,
      onError: {
        message: `"${pSequenceName}" → command #${pInstructionIndex}`,
        options: lOptions,
      },
      onSuccess: {
        message: lSuccess,
      }
    };
  }
  return null;
};


Validator.prototype._validatePneumatics = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  const lConfigManager = this.mLibrary.getConfigManager();
  const lAxisUUID = pInstruction.mParameters.axis;
  const lAxis = lConfigManager.getAxisByUuid(lAxisUUID);
  const lAxisParameters = pInstruction._getInstructionModel().parameters.find(item => item.name === "axis");
  const lCompatibleAxisList = pInstruction._listCompatibleAxisUuids(lAxisParameters);
  let lActuatorTypeName = '';
  if (lAxisParameters.allowedAxisType.includes('pneumatic_bag_gripper')) {
    lActuatorTypeName = 'Bag Gripper';
  }
  else if (lAxisParameters.allowedAxisType.includes('ac_motor_with_vfd')) {
    lActuatorTypeName = 'AC Motor';
  }
  else {
    lActuatorTypeName = 'Pneumatic Actuator';
  }
  // no actuator configured
  if (!lAxis) {
    // add an actuator in the config
    const lOptions = [{ message: `Add a ${lActuatorTypeName} in the Configuration tab.`, target: 'redirect-config' }];

    // if there are compatible actuators => select a compatible actuator from list
    if (lCompatibleAxisList.length > 0) {
      lOptions.unshift({ message: `Select a ${lActuatorTypeName} from the drop down menu.`, target: "actuator" });
    }

    // if there are other axes AND no other compatible actuators
    if (lConfigManager.numberOfRealAxes() && lCompatibleAxisList.length == 0) {
      lOptions.unshift({ message: 'Select available Motion from the drop down menu.', target: 'motion' });
      lOptions.unshift({ message: 'Select available Actuator from the drop down menu.', target: 'actuator' });
    }

    return {
      header: `No ${lActuatorTypeName} configured.`,
      onError: {
        message: `"${pSequenceName}" → command #${pInstructionIndex}`,
        options: lOptions
      },
      onSuccess: {
        message: `You do not have any missing ${lActuatorTypeName}`
      }
    };
  }

  // incompatible actuator
  const lIsAxisCompatible = lAxis && lCompatibleAxisList.includes(lAxis.getUUID());

  if (!lIsAxisCompatible) {
    // add an actuator in the config
    const lOptions = [{ message: `Add a ${lActuatorTypeName} in the Configuration tab.`, target: 'redirect-config' }];

    // if there are compatible actuators => select a compatible actuator from list
    if (lCompatibleAxisList.length > 0) {
      lOptions.unshift({ message: `Select a ${lActuatorTypeName} from the drop down menu.`, target: "actuator" });
    }

    // if we have an axis => propose another motion type
    if (lConfigManager.numberOfRealAxes()) {
      lOptions.unshift({ message: 'Select available Motion from the drop down menu.', target: 'motion' });
    }

    return {
      header: "Incompatible Actuator type configured.",
      onError: {
        message: `"${pSequenceName}" → command #${pInstructionIndex}`,
        options: lOptions
      },
      onSuccess: {
        message: `You do not have any missing ${lActuatorTypeName}`
      }
    };
  }
  // Missing Pneumatic
  const lPneumatic = lConfigManager.getAxisByUuid(pInstruction.getParameter('axis'));

  // Unconfigured position sensors, we cannot wait for motion completion.
  if (pInstruction.mParameters.waitOnMotionCompletion == 'true') {
    if (pInstruction.mName == 'push' && lPneumatic.mPins.pushInPin == null ||
      pInstruction.mName == 'pull' && lPneumatic.mPins.pullInPin == null ||
      pInstruction.mName == 'close' && lPneumatic.mPins.pushInPin == null ||
      pInstruction.mName == 'open' && lPneumatic.mPins.pullInPin == null
    ) {
      return {
        header: 'Unconfigured position sensor.',
        onError: {
          message: `"${pSequenceName}" → command #${pInstructionIndex}, missing ${pInstruction.mName}`,
          options: [
            { message: 'Uncheck "Wait For This Motion To Complete."' },
            { message: `Add a ${pInstruction.mName} position sensor to the selected pneumatic Actuator in the Configuration tab.`, target: 'redirect-config' }
          ]
        },
        onSuccess: {
          message: 'You do not have any unconfigured pneumatic position sensors.'
        }
      };
    }
  }

  return null;
};


Validator.prototype._validateOutputs = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  const lConfigManager = this.mLibrary.getConfigManager();
  const lUUID = pInstruction.getParameter('outputUuid');
  const lOutput = lConfigManager.getOutputByUuid(lUUID);

  if (!lOutput) {
    const options = [{ message: 'Add an Output in the Configuration tab.', target: 'redirect-config' }];
    if (lConfigManager.listOutputs().length) { options.unshift({ message: 'Select an Output from the drop down menu.', target: 'outputName' }); }

    return {
      header: 'Missing associated Output.',
      onError: {
        message: `"${pSequenceName}" → command #${pInstructionIndex}`,
        options: options
      },
      onSuccess: {
        message: 'You do not have any missing Output.'
      }
    };
  }
  return null;
};

Validator.prototype.validateOneFsmLimit = validateOneFsmLimit;

Validator.prototype.validateFsm = validateFsm;

Validator.prototype.validateWaitForInputs = function(pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  const lConfigManager = this.mLibrary.getConfigManager();
  const lUUID = pInstruction.getParameter('inputUuid');
  const lInputExists = lUUID && lConfigManager.getInputByUuid(lUUID);

  // checks if the current UUID is in the config
  const lMatchingUUID = ['waitFor'].includes(pInstruction.mCategory) && lInputExists;

  // missing associated input UUID
  if (!lMatchingUUID) {
    const options = [{ message: 'Add an Input in the Configuration tab.', target: 'redirect-config' }];
    if (lConfigManager.listInputs().length) {
      options.unshift({ message: 'Select an Input from the drop down menu.', target: 'inputName' });
    }

    return {
      header: 'Missing associated Input.',
      onError: {
        message: `"${pSequenceName}" → command #${pInstructionIndex}`,
        options: options
      },
      onSuccess: {
        message: 'You do not have any missing Input.'
      }
    };
  }
  return null;
};

Validator.prototype.validateSetVariableInputs = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  const lConfigManager = this.mLibrary.getConfigManager();
  const lUUID = pInstruction.getParameter('inputUuid');
  const lInputExists = lUUID && lConfigManager.getInputByUuid(lUUID);
  const lPushOrPullUUID = lUUID && lConfigManager.listAxes().find(axis => lUUID.includes(axis.getUUID()))?.isNonMotorized();
  // checks if the current UUID is in the config
  const lMatchingUUID = ['setVariable'].includes(pInstruction.mCategory) && (lInputExists || lPushOrPullUUID);

  // checks if there are any compatible pneumatic actuators in the config
  const lPneumaticAxesWithInputs = lConfigManager.listAxes().filter(axis => {
    return axis.isNonMotorized() && (typeof axis.mPins.pushInPin === "number" || typeof axis.mPins.pullInPin === "number");
  });

  // missing associated input UUID
  if (!lMatchingUUID) {
    const options = [{ message: 'Add an Input or a Pneumatic Actuator with an Input in the Configuration tab.', target: 'redirect-config' }];
    if (lConfigManager.listInputs().length || lPneumaticAxesWithInputs.length) {
      options.unshift({ message: 'Select an Input from the drop down menu.', target: 'inputName' });
    }

    return {
      header: 'Missing associated Input.',
      onError: {
        message: `"${pSequenceName}" → command #${pInstructionIndex}`,
        options: options
      },
      onSuccess: {
        message: 'You do not have any missing Input.'
      }
    };
  }
  return null;
};


Validator.prototype._validateConditions = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  // NOTE: we only check for input variables for now.
  const lConfigManager = this.mLibrary.getConfigManager();

  function isInputVariable(pName, pVariables) {
    const lFound = Object.values(pVariables).find(function (pEl) {
      if (!pEl.hasOwnProperty('inputUuid')) { return false; }
      return pEl.name == pName;
    });
    return lFound ? { uuid: lFound.inputUuid } : null;
  }

  if ('if' === pInstruction.mName) {
    // get all the input variables in the if-statements, build a map with indexes so we can find them later
    const lVarNamePositionUuidMap = pInstruction.mParameters.conditions.reduce(function (pAccumulator, pCurrent, pIfIndex) {
      pCurrent.conditionExpression.split(' ').forEach(function (pEl, pVarIndex) {
        const lInput = isInputVariable(pEl, pInstruction.mApp.mParameters.mParameters);
        if (lInput) {
          // {name: "ALICE", ifIndex: 0, varIndex: 0, uuid: 'a-b-c-d'}
          pAccumulator.push({ name: pEl, ifIndex: pIfIndex, varIndex: pVarIndex, uuid: lInput.uuid });
        }
      });
      return pAccumulator;
    }, []);

    // get all input variable that are NOT in Inputs
    const lMissingVarNamePositionUuidMap = lVarNamePositionUuidMap.filter(function (pVar) {
      const lExist = lConfigManager.getInputByUuid(pVar.uuid);
      return !lExist;
    });

    if (0 === lMissingVarNamePositionUuidMap.length) { return null; }

    const result = lMissingVarNamePositionUuidMap.map(function (pEl) {
      const lI = pEl.ifIndex * 2 + (pEl.varIndex == 0 ? 0 : 1);
      return {
        header: 'Missing Input tracked by Variable.',
        onError: {
          message: `"${pSequenceName}" → command #${pInstructionIndex}`,
          options: [
            { message: 'Update the variable.', target: `${lI}.value /Variable` },
            { message: 'Update the tracked Input in the Variables tab.', target: `redirect-variables.${pEl.name}` },
          ]
        },
        onSuccess: {
          message: 'The Variable is now valid.'
        },
        instructionSubResource: lI
      };
    });
    return result;
  }
  return null;
};


Validator.prototype._validateExpression = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  const lConfigManager = this.mLibrary.getConfigManager();
  const lErroneousVariables = [];

  Object.values(pInstruction.mApp.mParameters.mParameters).forEach((pVar) => {
    if (pVar.type == 'input') {
      const lLinkedInput = lConfigManager.getInputByUuid(pVar.inputUuid);
      if (lLinkedInput == undefined) { lErroneousVariables.push(pVar.name); }
    }
  });

  if ('fromExpression' == pInstruction.mName || 'fromExpressionArray' == pInstruction.mName) {
    const lErroneousVariable = lErroneousVariables.find((pVar) => {
      return pInstruction.mParameters.expression.split(/[ \+\-\*\/]/).includes(pVar);
    });

    if (lErroneousVariable != undefined) {
      return {
        header: `Missing Input tracked by Variable (${lErroneousVariable}).`,
        onError: {
          message: `"${pSequenceName}" → command #${pInstructionIndex}`,
          options: [
            { message: 'Update the expression.', target: `expression` },
            { message: 'Update the tracked Input in the Variables tab.', target: `redirect-variables.${lErroneousVariable}` },
          ]
        },
        onSuccess: {
          message: 'The Variable is now valid.'
        }
      };
    }
  }
  return null;
};

Validator.prototype._validateCondition = function(pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  // condition can have missing sequences and missing input variables
  const a = validateSequences(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  const b = this._validateConditions(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  if (a.length === 0 && b == null) {
    return null;
  }
  let out = [];
  if (a) {
    out = out.concat(a);
  }
  if (b) {
    out = out.concat(b);
  }

  return out;
};

Validator.prototype._validateLog = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  if ('console' == pInstruction.mName) {
    return this._validateConsole(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  return null;
};

Validator.prototype._validateMotor = function(pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  if (['open', 'close', 'push', 'pull', 'idle', 'moveVFD', 'stopVFD'].includes(pInstruction.getName())) {
    return this._validatePneumatics(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  if (['stopAllMotion', 'setAcceleration', 'setSpeed'].includes(pInstruction.getName())) {
    // those apply to all actuators, no need to validate
    return null;
  }
  // If there are no available actuators, propose to fix it right away.
  const lConfigManager = pInstruction.mApp.mLibrary.getConfigManager();
  if (0 == (lConfigManager.numberOfAxes())) {
    return {
      header: 'No Actuator configured.',
      onError: {
        message: "You don't have any Actuators configured.",
        options: [{ message: 'Add an Actuator in the Configuration tab.', target: 'redirect-config' }]
      },
      onSuccess: {
        message: "You don't have any missing Actuator."
      }
    };
  }
  if (pInstruction.getName().match(/Combined/i)) {
    return this._validateCombinedMoves(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  return this._validateSingleMove(pInstruction, pAppName, pSequenceName, pInstructionIndex);
};


Validator.prototype._validateRobot = function(pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  // If there are no available robots, propose to fix it right away.
  const lConfigManager = pInstruction.mApp.mLibrary.getConfigManager();
  if (0 === lConfigManager.numberOfRobots()) {
    return {
      header: "No Robot is configured.",
      onError: {
        message: "You don't have a Robot configured.",
        options: [
          {
            message: "Add a Robot in the Configuration tab.",
            target: "redirect-config",
          },
        ],
      },
      onSuccess: {
        message: "You don't have a missing Robot.",
      },
    };
  }
  return null;
};

Validator.prototype._validateSetVariable = function(pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  if ('fromDevice' == pInstruction.mName) {
    return this.validateSetVariableInputs(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  else if ('fromExpression' == pInstruction.mName) {
    return this._validateExpression(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  else if ('fromExpressionArray' == pInstruction.mName) {
    return this._validateExpression(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  else if ('fromActuatorPosition' == pInstruction.mName) {
    return this._validateNonMotorAxisInstruction(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  return null;
};

Validator.prototype._validateWaitFor = function(pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  if (['digitalInput', 'digitalInputEdge'].includes(pInstruction.mName)) {
    return this.validateWaitForInputs(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  else if ('motionCompletion' === pInstruction.mName) {
    return this._validateNonMotorAxisInstruction(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  return null;
};

Validator.prototype._validateConsole = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  const lConfigManager = this.mLibrary.getConfigManager();
  const lRegEx = /\${(\S+)}/g;
  const lMessage = "" + pInstruction.mParameters.message;
  let lRegResult = lRegEx.exec(lMessage);
  const lParams = pInstruction.mApp.mParameters.mParameters;
  const lMissing = [];
  let lIndex = 0;

  while (lRegResult) {
    const lVarName = lRegResult[1].split('(')[0];

    // Note Audrey : We do not check for variable names that are not defined, as this is prevented by the variable tab UI/UX (so-called "variable lock logic")
    // If we wanted to, we could so by checking if lUserVar is undefined.

    const lUserVar = Object.values(lParams).find(function (pEl) { return pEl.name == lVarName; });
    if (lVarName && lUserVar.type == 'input' && !lConfigManager.getInputByUuid(lUserVar.inputUuid)) {
      lMissing.push({
        header: `Missing Input tracked by Variable (${lVarName}).`,
        onError: {
          message: `"${pSequenceName}" → command #${pInstructionIndex}`,
          options: [
            // NOTE: we target Message, but due to the camelCaseToText transformation (which all add space) ...
            { message: 'Update the variable.', target: `message` },
            { message: 'Update the tracked Input in the Variables tab.', target: `redirect-variables.${lUserVar.name}` },
          ]
        },
        onSuccess: {
          message: 'The Variable is now valid.'
        },
        // NOTE: we can multiple error for the same instruction, need this to make them different
        instructionSubResource: lIndex,
      });
    }
    lRegResult = lRegEx.exec(lMessage);
    lIndex++;
  }

  return lMissing.length ? lMissing : null;
};


Validator.prototype.validate = function (pInstruction, pAppName, pSequenceName, pInstructionIndex) {
  pInstructionIndex += 1; // normal people start to count from one.

  // NOTE: available mCategories -- ["motor", "waitFor", "trigger", "loop", "output", "setVariable", "condition", "config", "log"]
  if ('motor' === pInstruction.mCategory) {
    return this._validateMotor(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  // Missing Outputs
  if ('output' === pInstruction.mCategory && ['digitalOutput'].includes(pInstruction.mName)) {
    return this._validateOutputs(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  // Missing Inputs
  if ('waitFor' === pInstruction.mCategory) {
    return this._validateWaitFor(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  if('fsm' === pInstruction.mCategory) {
    return this.validateFsm(pInstruction, pAppName, pSequenceName, pInstructionIndex, this.mLibrary.getConfigManager());
  }
  // Missing child sequence
  if (['trigger', 'loop', 'robotLoop'].includes(pInstruction.mCategory)) {
    // if action does not have parameters, it does not need to be validated any more
    const lParameters = pInstruction.listParameters();
    if (lParameters.length === 0) { return null; }
    return validateSequences(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  if ('condition' === pInstruction.mCategory) {
    return this._validateCondition(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  if ('setVariable' === pInstruction.mCategory) {
    return this._validateSetVariable(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  if ('log' === pInstruction.mCategory) {
    return this._validateLog(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }

  if ('robot' === pInstruction.mCategory && 'simulate' === pInstruction.mName) {
    return this._validateRobot(pInstruction, pAppName, pSequenceName, pInstructionIndex);
  }
  // for dev
  // console.warn(`Not supported type for validation, [${pInstruction.mCategory} > ${pInstruction.mName}]`, pInstruction);

  return null;
};

Validator.prototype.addValidateAppCallback = function(callbackName, callback) {
  this.mValidateAppCallbacks[callbackName] = callback;
};

Validator.prototype.removeValidateAppCallback = function(callbackName) {
  delete this.mValidateAppCallbacks[callbackName];
};

Validator.prototype.isValidateAppCallbackDefined = function(callbackName) {
  return this.mValidateAppCallbacks[callbackName];
};

Object.defineProperty(
  Validator.prototype,
  'cache',
  {
    get: function () {
      return this.mCache;
    }
  }
);